import {
  authEndpoints,
  changeLocale,
  SUPPORTED_LOCALES,
  toast,
  useAuthStore,
  useThemeStore,
  type Locale,
  type ThemeMode,
} from '@sentinel/web-core';
import { useTranslation } from 'react-i18next';

const MODES: readonly ThemeMode[] = ['light', 'dark', 'auto'];

/** Saved in the profile when possible; the choice holds in this browser anyway. */
async function savePreference(patch: { theme?: ThemeMode; locale?: Locale }): Promise<void> {
  const { status, setMe } = useAuthStore.getState();
  if (status !== 'authenticated') return;
  try {
    setMe(await authEndpoints.preferences(patch));
  } catch {
    /* kept locally: the profile is updated at the next change */
  }
}

/** Theme and language in the user card: both change at runtime, without reloading (§3, §7). */
export function Appearance() {
  const { t, i18n } = useTranslation();
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  const chooseTheme = (next: ThemeMode) => {
    setMode(next);
    toast('info', t(`core.theme.${next}`), t(`core.theme.${next}Detail`));
    void savePreference({ theme: next });
  };
  const chooseLocale = async (locale: Locale) => {
    await changeLocale(locale);
    toast('info', t('core.language.changed', { language: t(`core.language.${locale}`) }));
    await savePreference({ locale });
  };

  return (
    <div className="uc-sec">
      <div className="uc-row" role="group" aria-labelledby="uc-theme">
        <span className="mlabel" id="uc-theme">
          {t('shell.user.theme')}
        </span>
        <span className="seg">
          {MODES.map((m) => (
            <button key={m} type="button" aria-pressed={mode === m} onClick={() => chooseTheme(m)}>
              {t(`shell.user.themes.${m}`)}
            </button>
          ))}
        </span>
      </div>
      <div className="uc-row" role="group" aria-labelledby="uc-lang">
        <span className="mlabel" id="uc-lang">
          {t('core.language.label')}
        </span>
        <span className="seg">
          {SUPPORTED_LOCALES.map((locale) => (
            <button
              key={locale}
              type="button"
              lang={locale}
              aria-pressed={i18n.language === locale}
              onClick={() => void chooseLocale(locale)}
            >
              {t(`core.language.${locale}`)}
            </button>
          ))}
        </span>
      </div>
    </div>
  );
}
